import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Trash2, X, BadgeCheck, FileText, Copy, Ban, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import { downloadPdf } from "../../lib/download";

const TYPE_STYLES = {
  "Press": "bg-[#eef2ff] text-[#4f46e5]",
  "Event Staff": "bg-[#eefafa] text-[#1fb0b0]",
  "Artist/Guest": "bg-[#fdf2f8] text-[#db2777]",
  "Crew": "bg-[#fff7ed] text-[#ea580c]",
  "VIP": "bg-[#fefce8] text-[#a16207]",
};

export default function PassesTab() {
  const [passes, setPasses] = useState(null);
  const [types, setTypes] = useState(["Press", "Event Staff", "Artist/Guest", "Crew", "VIP"]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ holder_name: "", pass_type: "Press", expires_at: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/business/passes").then(({ data }) => setPasses(data.passes || [])).catch(() => setPasses([]));
  useEffect(() => {
    load();
    api.get("/business/pass-types").then(({ data }) => setTypes(data.types || types)).catch(() => {});
  }, []); // eslint-disable-line

  const create = async () => {
    if (!form.holder_name.trim()) { toast({ title: "Holder name is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/business/passes", { holder_name: form.holder_name.trim(), pass_type: form.pass_type, expires_at: form.expires_at || null });
      toast({ title: "Pass created", description: `${form.pass_type} pass for ${form.holder_name.trim()}.` });
      setShowAdd(false); setForm({ holder_name: "", pass_type: "Press", expires_at: "" }); load();
    } catch (e) { toast({ title: "Couldn't create", description: e?.response?.data?.detail || "Try again.", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const revoke = async (p) => {
    if (!window.confirm(`Revoke ${p.holder_name}'s ${p.pass_type} pass? It will scan as INVALID at every door.`)) return;
    await api.post(`/business/passes/${p.id}/revoke`); toast({ title: "Pass revoked" }); load();
  };
  const reinstate = async (p) => { await api.post(`/business/passes/${p.id}/reinstate`); toast({ title: "Pass reinstated" }); load(); };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.holder_name}'s pass permanently?`)) return;
    await api.delete(`/business/passes/${p.id}`); toast({ title: "Pass deleted" }); load();
  };
  const getPdf = async (p) => {
    try { await downloadPdf(`/business/passes/${p.id}/pass.pdf`, `pass-${p.code}.pdf`); }
    catch { toast({ title: "Couldn't generate PDF", variant: "destructive" }); }
  };
  const copyLink = (p) => {
    const url = `${window.location.origin}/pass/${p.code}`;
    navigator.clipboard.writeText(url).then(() => toast({ title: "Pass link copied", description: url }));
  };

  if (!passes) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={28} /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[#5a6b80] text-sm max-w-2xl">Issue <strong>Press &amp; Event Staff passes</strong> that give free access to <strong>all your live events</strong>. Each pass has a QR that scans green as "VALID" at any door — reusable and never used up. Passes stay valid until you revoke them.</p>
        <button onClick={() => setShowAdd(true)} data-testid="add-pass-btn" className="gp-btn-teal rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 shrink-0"><Plus size={16} /> Create pass</button>
      </div>

      {passes.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-[#8a97a8]" data-testid="passes-empty"><BadgeCheck className="mx-auto mb-3 text-[#c7d2e0]" size={32} />No passes yet. Create a Press or Event Staff pass to hand out.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {passes.map((p) => (
            <div key={p.id} className={`bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)] ${!p.active ? "opacity-70" : ""}`} data-testid={`pass-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${TYPE_STYLES[p.pass_type] || "bg-gray-100 text-gray-600"}`}>{p.pass_type}</span>
                    {p.active && p.expired
                      ? <span className="text-xs font-semibold text-amber-500" data-testid={`pass-status-${p.id}`}>● EXPIRED</span>
                      : p.active
                        ? <span className="text-xs font-semibold text-green-600" data-testid={`pass-status-${p.id}`}>● VALID</span>
                        : <span className="text-xs font-semibold text-red-500" data-testid={`pass-status-${p.id}`}>● REVOKED</span>}
                  </div>
                  <p className="font-semibold text-[#16243b] mt-2 truncate">{p.holder_name}</p>
                  <p className="text-xs text-[#8a97a8] font-mono mt-0.5">{p.code}</p>
                  <p className="text-[11px] text-[#8a97a8] mt-1" data-testid={`pass-expiry-${p.id}`}>{p.expires_at ? `Expires ${new Date(p.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : "No expiry — valid until revoked"}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                <button onClick={() => getPdf(p)} data-testid={`pass-pdf-${p.id}`} className="gp-btn-outline rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"><FileText size={13} /> PDF</button>
                <a href={`/pass/${p.code}`} target="_blank" rel="noreferrer" data-testid={`pass-view-${p.id}`} className="gp-btn-outline rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"><ExternalLink size={13} /> View</a>
                <button onClick={() => copyLink(p)} data-testid={`pass-copy-${p.id}`} className="gp-btn-outline rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5"><Copy size={13} /> Link</button>
                {p.active
                  ? <button onClick={() => revoke(p)} data-testid={`pass-revoke-${p.id}`} className="rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 border border-red-200 text-red-500 hover:bg-red-50"><Ban size={13} /> Revoke</button>
                  : <button onClick={() => reinstate(p)} data-testid={`pass-reinstate-${p.id}`} className="rounded-lg px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 border border-[#2ec9c9] text-[#1fb0b0] hover:bg-[#eefafa]"><RotateCcw size={13} /> Reinstate</button>}
                <button onClick={() => remove(p)} data-testid={`pass-delete-${p.id}`} title="Delete" className="rounded-lg px-2.5 py-1.5 text-xs text-red-400 hover:bg-red-50 hover:text-red-600 ml-auto"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-pass-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-[#16243b] text-lg">Create access pass</h3><button onClick={() => setShowAdd(false)}><X size={18} className="text-[#8a97a8]" /></button></div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Pass type</label>
                <select value={form.pass_type} onChange={(e) => setForm({ ...form, pass_type: e.target.value })} data-testid="pass-type-select" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]">
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Holder name</label>
                <input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} data-testid="pass-holder-input" placeholder="e.g. Jane Reporter — BBC" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#8a97a8] mb-1.5 block">Expiry date <span className="text-[#b6c0cd]">(optional)</span></label>
                <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="pass-expiry-input" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
                <p className="text-[11px] text-[#8a97a8] mt-1">Leave blank to stay valid until you revoke it. The pass auto-invalidates at the end of this day.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAdd(false)} className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">Cancel</button>
              <button onClick={create} disabled={busy} data-testid="pass-create-confirm" className="gp-btn-teal rounded-xl px-6 py-2.5 text-sm font-medium disabled:opacity-60 flex items-center gap-2">{busy ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
